/**
 * Alta y edición de una programación.
 *
 * Es la misma pantalla para las dos cosas: los campos son idénticos y sólo cambia de
 * dónde salen los valores iniciales y a qué endpoint se manda. Tenerlas separadas
 * garantizaba que un campo nuevo apareciera en una y no en la otra.
 *
 * No hay vista previa acá a propósito. Una programación **recalcula sobre los precios del
 * día en que corre**, así que una vista previa de hoy diría números que en tres semanas
 * son falsos. Es exactamente lo contrario de una actualización manual, donde el preview
 * es el plan que se ejecuta.
 */

import { Alert, Box, Button, Card, Input, Text } from '@nimbus-ds/components';
import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  api,
  type Ajuste,
  type Alcance,
  type CampoAfectado,
  type Recurrencia,
  type Redondeo,
  type Regla,
} from '../api';
import {
  ALCANCE_INICIAL,
  CamposDeAjuste,
  CamposDeAlcance,
  sinCriterio,
} from '../componentes/CamposDeRegla';
import { CamposDeRecurrencia, RECURRENCIA_INICIAL } from '../componentes/CamposDeRecurrencia';
import { Cargando, ErrorCaja } from '../componentes/Estado';
import { Layout } from '../componentes/Layout';
import { aErrorVisible, type ErrorVisible } from '../errores';
import { useSesion } from '../sesion';

export function FormularioProgramacion(): ReactElement {
  const { id } = useParams();
  const editando = id !== undefined;
  const navigate = useNavigate();

  const [nombre, setNombre] = useState('Aumento mensual');
  const [recurrencia, setRecurrencia] = useState<Recurrencia>(RECURRENCIA_INICIAL);
  const [alcance, setAlcance] = useState<Alcance>(ALCANCE_INICIAL);
  const [ajuste, setAjuste] = useState<Ajuste>({ kind: 'percentage', percent: 12 });
  const [redondeo, setRedondeo] = useState<Redondeo>({ kind: 'none' });
  const [campos, setCampos] = useState<CampoAfectado>('price');

  const sesion = useSesion();
  // Editar una programación que ya existe se permite siempre: es limpieza. Lo que se
  // bloquea es crear una nueva, que es empezar corridas nuevas.
  const bloqueada = !editando && sesion !== null && !sesion.acceso.puedeActualizar;

  const [cargando, setCargando] = useState(editando);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<ErrorVisible | null>(null);

  useEffect(() => {
    if (id === undefined) return;

    let vigente = true;
    api
      .programacion(id)
      .then(({ programacion }) => {
        if (!vigente) return;
        setRecurrencia(programacion.recurrencia);
        if (programacion.nombre !== null) setNombre(programacion.nombre);
        if (programacion.regla !== null) {
          setAlcance(programacion.regla.scope);
          setAjuste(programacion.regla.adjustment);
          setRedondeo(programacion.regla.rounding);
          setCampos(programacion.regla.targetFields);
        } else {
          // La regla fue borrada. La programación existe y hay que poder arreglarla, así
          // que el formulario arranca en blanco y se avisa.
          setError({
            mensaje:
              'La regla de esta programación ya no existe. Elegí de nuevo qué productos y cuánto cambia antes de guardar.',
            codigo: null,
            tipo: null,
          });
        }
      })
      .catch((excepcion: unknown) => {
        if (vigente) setError(aErrorVisible(excepcion, 'No pudimos cargar esta programación.'));
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [id]);

  const falta = sinCriterio(alcance);
  const sinNombre = nombre.trim() === '';

  async function guardar(): Promise<void> {
    setGuardando(true);
    setError(null);
    const regla: Regla = {
      scope: alcance,
      adjustment: ajuste,
      rounding: redondeo,
      targetFields: campos,
    };
    try {
      if (id === undefined) await api.crearProgramacion({ nombre, recurrencia, regla });
      else await api.editarProgramacion(id, { nombre, recurrencia, regla });
      navigate('/programaciones');
    } catch (excepcion) {
      setError(aErrorVisible(excepcion, 'No pudimos guardar la programación.'));
      setGuardando(false);
    }
  }

  if (cargando) {
    return (
      <Layout titulo="Programación">
        <Cargando />
      </Layout>
    );
  }

  return (
    <Layout titulo={editando ? 'Editar programación' : 'Nueva programación'}>
      {error !== null && <ErrorCaja mensaje={error.mensaje} codigo={error.codigo} />}

      <Card>
        <Card.Header title="Ponele un nombre" />
        <Card.Body>
          <Input
            id="nombre"
            name="nombre"
            value={nombre}
            onChange={(evento) => setNombre(evento.currentTarget.value)}
            placeholder="Aumento mensual"
          />
        </Card.Body>
      </Card>

      <CamposDeRecurrencia valor={recurrencia} onValor={setRecurrencia} />
      <CamposDeAlcance alcance={alcance} onAlcance={setAlcance} />
      <CamposDeAjuste
        ajuste={ajuste}
        redondeo={redondeo}
        campos={campos}
        onAjuste={setAjuste}
        onRedondeo={setRedondeo}
        onCampos={setCampos}
      />

      {falta && (
        <Alert appearance="warning" title="Falta elegir qué productos">
          <Text>Elegí al menos una categoría, una marca o un rango de precios.</Text>
        </Alert>
      )}

      <Alert appearance="neutral" title="Se recalcula el día que corre">
        <Text>
          Cada vez que se dispare, la regla se aplica sobre los precios de ese momento. Es
          &quot;+12% sobre lo que valga ese día&quot;, no sobre lo que vale hoy.
        </Text>
      </Alert>

      <Box
        className="acciones-a-lo-ancho"
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        gap={{ xs: '3', md: '2' }}
        flexWrap="wrap"
      >
        <Button onClick={() => navigate('/programaciones')}>Cancelar</Button>
        <Button
          size="large"
          appearance="primary"
          disabled={guardando || falta || sinNombre || bloqueada}
          onClick={() => void guardar()}
        >
          {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear programación'}
        </Button>
      </Box>
    </Layout>
  );
}
